/**
 * Google Drive thumbnail fetching utilities for gallery media items
 * 
 * Provides on-demand thumbnail fetching from Google Drive API for videos
 * that don't have thumbnails stored in the database.
 */

import { MediaItem } from '@/models/media-item';

// Cache for fetched Drive thumbnails to avoid repeated API calls
const thumbnailCache = new Map<string, string>();

/**
 * Extract Google Drive file ID from various URL formats
 */
export function extractDriveFileId(url: string): string | null {
  if (!url) return null;

  const patterns = [
    /\/file\/d\/([a-zA-Z0-9_-]+)/,  // /file/d/FILE_ID/...
    /[?&]id=([a-zA-Z0-9_-]+)/,      // ?id=FILE_ID or &id=FILE_ID
    /\/uc\?id=([a-zA-Z0-9_-]+)/,    // /uc?id=FILE_ID
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Check if a media item is a Google Drive video
 */
export function isDriveVideo(media: MediaItem): boolean {
  if (media.type !== 'video') return false;
  if (media.videoProvider === 'google-drive') return true;
  
  // Check URL patterns if provider not set
  const url = media.videoUrl || media.url;
  if (!url) return false;
  
  return /drive\.google\.com/.test(url);
}

/**
 * Fetch thumbnail URL from Google Drive API for a file ID
 */
export async function fetchDriveThumbnail(fileId: string): Promise<string | null> {
  // Check cache first
  if (thumbnailCache.has(fileId)) {
    return thumbnailCache.get(fileId)!;
  }

  try {
    const response = await fetch(`/api/google-drive?fileId=${encodeURIComponent(fileId)}`);
    
    if (!response.ok) {
      console.error(`Failed to fetch Drive thumbnail for ${fileId}:`, response.statusText);
      return null;
    }

    const data = await response.json();
    
    if (data.type === 'file' && data.videos && data.videos.length > 0) {
      const thumbnailUrl = data.videos[0].thumbnailUrl;
      if (thumbnailUrl) {
        // Cache the result
        thumbnailCache.set(fileId, thumbnailUrl);
        return thumbnailUrl;
      }
    }
  } catch (error) {
    console.error(`Error fetching Drive thumbnail for ${fileId}:`, error);
  }

  return null;
}

/**
 * Check if a thumbnail URL is a Drive link that needs proxying
 */
export function isDriveThumbnailLink(url: string): boolean {
  if (!url) return false;
  return url.includes('drive.google.com/thumbnail') ||
         url.includes('lh3.googleusercontent.com') ||
         url.includes('googleusercontent.com/');
}

/**
 * Convert a Drive thumbnail link to use our proxy
 */
export function convertToProxyUrl(thumbnailUrl: string, fileId?: string): string {
  // If we have a fileId, use it directly
  if (fileId) {
    return `/api/google-drive/thumbnail?fileId=${fileId}`;
  }

  // Try to extract file ID from the thumbnail URL
  const idMatch = thumbnailUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch && idMatch[1]) {
    return `/api/google-drive/thumbnail?fileId=${idMatch[1]}`;
  }

  // If we can't extract ID, return the original URL (will fail, but better than nothing)
  console.warn('Could not convert Drive thumbnail URL to proxy:', thumbnailUrl);
  return thumbnailUrl;
}

/**
 * Get thumbnail URL for a media item, fetching from Drive API if needed
 *
 * This function:
 * 1. For Drive videos, uses videoId field or extracts from URL
 * 2. Returns proxy URL directly for Drive videos
 * 3. Checks if stored thumbnailUrl is a Drive link, converts to proxy if so
 * 4. Returns stored thumbnail for non-Drive videos
 *
 * @param media - Media item to get thumbnail for
 * @returns Promise resolving to thumbnail URL or null
 */
export async function getMediaThumbnail(media: MediaItem): Promise<string | null> {
  // For Drive videos, construct proxy URL directly using videoId field
  if (isDriveVideo(media)) {
    // Use videoId field if available (already extracted and stored)
    const fileId = media.videoId || extractDriveFileId(media.videoUrl || media.url);
    if (fileId) {
      return `/api/google-drive/thumbnail?fileId=${fileId}`;
    }
    console.warn('Could not get Drive file ID for video:', media);
    return null;
  }

  // If thumbnail is a Drive link (legacy data), convert to proxy URL
  if (media.thumbnailUrl && isDriveThumbnailLink(media.thumbnailUrl)) {
    const fileId = media.videoId || extractDriveFileId(media.videoUrl || media.url);
    return convertToProxyUrl(media.thumbnailUrl, fileId || undefined);
  }

  // If thumbnail already stored and not a Drive link, use it
  if (media.thumbnailUrl) {
    return media.thumbnailUrl;
  }

  return null;
}

/**
 * Clear the thumbnail cache (useful for testing or memory management)
 */
export function clearThumbnailCache(): void {
  thumbnailCache.clear();
}

/**
 * Pre-fetch thumbnails for multiple media items
 * Useful for batch loading thumbnails for a gallery view
 * 
 * @param mediaItems - Array of media items to pre-fetch thumbnails for
 * @returns Promise that resolves when all thumbnails are fetched
 */
export async function prefetchDriveThumbnails(mediaItems: MediaItem[]): Promise<void> {
  const driveVideos = mediaItems.filter(
    media => isDriveVideo(media) && !media.thumbnailUrl
  );

  const fileIds = driveVideos
    .map(media => extractDriveFileId(media.videoUrl || media.url))
    .filter((id): id is string => id !== null);

  // Remove duplicates
  const uniqueFileIds = Array.from(new Set(fileIds));

  // Fetch all thumbnails in parallel (but limit concurrency to avoid rate limits)
  const BATCH_SIZE = 5;
  for (let i = 0; i < uniqueFileIds.length; i += BATCH_SIZE) {
    const batch = uniqueFileIds.slice(i, i + BATCH_SIZE);
    await Promise.all(batch.map(fileId => fetchDriveThumbnail(fileId)));
  }
}
